'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion, Variants } from 'framer-motion';
import { ArrowRight, Download, Sparkles } from 'lucide-react';

const HeroSection = () => {
      const fadeUp: Variants = {
            hidden: { opacity: 0, y: 30 },
            visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.8, ease: 'easeOut' }
            }
      };

      const staggerContainer: Variants = {
            visible: {
                  transition: { staggerChildren: 0.2 }
            }
      };

      const handleScrollToFeatures = () => {
            const element = document.getElementById('features');
            if (element) element.scrollIntoView({ behavior: 'smooth' });
      };

      return (
            <section className="relative mt-20 w-full min-h-dvh flex flex-col lg:flex-row overflow-hidden bg-[#FAFAFA]">
                  <div className="absolute left-0 top-0 w-2/4 lg:w-2/4 h-full z-0 pointer-events-none ml-40">
                        <Image
                              src="/hero-bg.svg"
                              alt="Background Pattern"
                              fill
                              className="object-cover object-top-left opacity-[0.15] lg:opacity-100 mix-blend-multiply"
                              priority
                        />
                  </div>

                  <div className="absolute right-0 top-1/4 w-125 h-125 bg-purple-400/20 rounded-full blur-[120px] pointer-events-none z-0 mix-blend-multiply" />
                  <div className="absolute left-10 bottom-10 w-75 h-75 bg-pink-400/20 rounded-full blur-[100px] pointer-events-none z-0" />

                  <div className="relative z-10 w-full lg:w-1/2 flex flex-col justify-center lg:items-end pt-32 pb-12 px-6 lg:px-0 lg:pl-10 lg:pr-6">
                        <motion.div
                              initial="hidden"
                              animate="visible"
                              variants={staggerContainer}
                              className="space-y-8 max-w-2xl w-full text-left"
                        >
                              <motion.h1 variants={fadeUp} className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 leading-[1.1] tracking-tight">
                                    Hayatın Merkezinde, <br />
                                    <span className="relative inline-block text-transparent bg-clip-text bg-linear-to-r from-[#6320EE] via-[#DA38E2] to-[#FF6B6B]">
                                          Ayrıcalıkların Zirvesi
                                          <svg className="absolute w-full h-3 -bottom-1 left-0 text-[#DA38E2] opacity-40" viewBox="0 0 200 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.00025 6.99997C25.7509 9.36111 114.333 8.16661 196 2" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>
                                    </span>
                              </motion.h1>

                              <motion.p variants={fadeUp} className="text-gray-600 text-lg md:text-xl leading-relaxed max-w-lg">
                                    Şehrin ritmini yakalayan kadınlar için tasarlanmış yeni nesil yaşam asistanı.
                                    <span className="font-semibold text-gray-800"> Gyno Club</span> ile statü, konfor ve size özel deneyimlerin kapılarını aralayın.
                              </motion.p>

                              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 pt-4">
                                    <Link href="https://gynoclub.com.tr/app" target='_blank' className="group relative px-8 py-4 rounded-full bg-gray-900 text-white font-semibold text-lg shadow-xl shadow-purple-500/20 hover:shadow-purple-500/40 hover:-translate-y-1 transition-all duration-300 overflow-hidden">
                                          <span className="absolute inset-0 w-full h-full bg-linear-to-r from-[#6320EE] to-[#DA38E2] opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out"></span>
                                          <span className="relative flex items-center justify-center gap-2">
                                                Hemen Keşfet <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                          </span>
                                    </Link>

                                    <button onClick={handleScrollToFeatures} className="group px-8 py-4 rounded-full bg-white border border-gray-200 text-gray-700 font-medium hover:border-purple-200 hover:bg-purple-50/50 hover:text-purple-700 transition-all duration-300 flex items-center justify-center gap-2">
                                          <Sparkles className="w-5 h-5 text-purple-400 group-hover:text-purple-600" />
                                          Neler Var?
                                    </button>
                              </motion.div>

                              <motion.div variants={fadeUp} className="hidden lg:flex items-center gap-5 mt-12 p-5 bg-white/60 backdrop-blur-xl border border-white/40 rounded-3xl w-fit shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow duration-300">
                                    <div className="relative w-20 h-20 bg-white p-2 rounded-xl shadow-sm border border-gray-100">
                                          <Image
                                                src="/app-qr.svg"
                                                alt="Download QR"
                                                width={80}
                                                height={80}
                                                className="object-contain"
                                          />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                          <div className="flex items-center gap-2 text-gray-900 font-bold text-base">
                                                <Download className="w-4 h-4 text-purple-600" />
                                                <span>Uygulamayı İndir</span>
                                          </div>
                                          <p className="text-sm text-gray-500 max-w-40 leading-snug">
                                                Kameranızı okutun, Gyno Club dünyasına adım atın.
                                          </p>
                                    </div>
                              </motion.div>
                        </motion.div>
                  </div>

                  <div className="relative w-full lg:w-1/2 min-h-125 lg:h-auto flex items-center justify-center lg:justify-start lg:pl-30">
                        <div className="relative flex items-center justify-center">
                              <motion.div
                                    animate={{
                                          scale: [1, 1.1, 1],
                                          opacity: [0.5, 0.7, 0.5]
                                    }}
                                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-87.5 h-87.5 md:w-150 md:h-150 rounded-full blur-[90px] z-0"
                                    style={{
                                          background: 'linear-gradient(135deg, rgba(99, 32, 238, 0.3) 0%, rgba(218, 56, 226, 0.3) 100%)'
                                    }}
                              />

                              <div className="relative z-10 w-70 md:w-85 lg:w-105">
                                    <motion.div
                                          initial={{ y: 50, opacity: 0 }}
                                          animate={{ y: 0, opacity: 1 }}
                                          transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
                                          className="relative"
                                    >
                                          <motion.div
                                                animate={{ y: [-10, 10, -10] }}
                                                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                                                className="relative w-full aspect-9/18 drop-shadow-2xl"
                                          >
                                                <Image
                                                      src="/heroPhone.svg"
                                                      alt="Gyno Club Interface"
                                                      fill
                                                      priority
                                                      className="object-contain drop-shadow-[0_20px_50px_rgba(84,26,198,0.25)]"
                                                />
                                                <div className="absolute inset-0 bg-linear-to-tr from-transparent via-white/10 to-transparent pointer-events-none rounded-[3rem]" />
                                          </motion.div>
                                    </motion.div>
                              </div>
                        </div>
                  </div>

                  <div className="absolute bottom-0 left-0 w-full h-32 bg-linear-to-t from-[#FAFAFA] via-[#FAFAFA]/90 to-transparent z-20 pointer-events-none" />

            </section>
      );
};

export default HeroSection;